import { useState, useEffect } from "react";
import {
  CreateInsuranceTransactionDto,
  ProcessedCorrectionBillingDto,
  RxPreviewDto,
} from "../../../../../../api-client";
import useStore from "../../../../../../helpers/useStore";
import { TransactionsContext } from "../../../../../../stores/Transactions/Transactions.provider";
import notificationStore from "../../../../../../stores/Notification/notificationStore";
import { transactionMethods } from "../../../../Kasse/v2/types/kasse.types";
import { BillingTypeEnum } from "../../../Base/types/insuranceBills.types";
import { CorrectBillInfoProps } from "../types/CorrectBill.types";

const useCorrectionBillInfo = (props: CorrectBillInfoProps) => {
  const {
    handleFullScreenDialogClose,
    onRowClick,
    account,
    userName,
    userId,
    onRxDelete,
    onValidationFilter,
    onBillsSaved,
    selectedRx,
  } = props;

  // UI state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [localSelectedRx, setLocalSelectedRx] = useState<any | null>(null);
  const [selectedRxId, setSelectedRxId] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openSingleRxDialog, setOpenSingleRxDialog] = useState(false);
  const [rxNumber, setRxNumberInput] = useState("");
  const [tableVisible, setTableVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Will pass to BillCard Component
  const [correctionBills, setCorrectionBills] = useState<
    ProcessedCorrectionBillingDto[]
  >([]);

  // Store
  const TransactionsStore = useStore(TransactionsContext);
  const {
    createTransaction, // Commit draft changes (save in the database)
    transactionRefresh, // Get the transactionRefresh flag from the TransactionsContext
    prepareCorrectionBillsFromRxs, // Function to process selected RXs from transaction search
  } = TransactionsStore;

  // Effect to refetch data when the selected Rx changes (from TransactionRxOverview)
  useEffect(() => {
    // Only proceed if we have valid selectedRx and correctionBills
    if (selectedRx && correctionBills.length > 0) {
      // Refetch data due to transactionRefresh changing
      const refetchData = async () => {
        try {
          setIsLoading(true);

          // Extract all current rxIds from bills, filtering out any undefined values
          const currentRxIds = correctionBills
            .flatMap((bill) => bill.rxs.map((rx) => rx.id))
            .filter(Boolean) as string[];

          if (currentRxIds.length > 0) {
            // Prepare new correction bills from the current Rx IDs
            const newPreparedBills = await prepareCorrectionBillsFromRxs({
              rxIds: currentRxIds,
              accountId: account,
            });

            // Update local state with fresh data
            setCorrectionBills(newPreparedBills);
          }
        } catch (error) {
          console.error("Error refetching correction data", error);
        } finally {
          setIsLoading(false);
        }
      };

      refetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionRefresh]);

  // Kebab menu
  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    rx: any,
  ) => {
    setAnchorEl(event.currentTarget);
    setLocalSelectedRx(rx);
  };
  const handleMenuClose = () => setAnchorEl(null);

  // Delete flow
  const handleDeleteClick = () => {
    setOpenDeleteDialog(true);
    handleMenuClose();
  };
  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
    setLocalSelectedRx(null);
  };

  function roundToNearestHundred(num: number): number {
    return Math.round(num * 100) / 100;
  }

  const handleDeleteConfirm = async () => {
    if (!localSelectedRx) return;

    try {
      // Create a new array with the updated bills - filter out the deleted Rx
      const updatedBills = correctionBills
        .map((bill) => {
          // Check if this bill contains the Rx we want to delete
          const rxToDelete = bill.rxs.find(
            (rx) => rx.id === localSelectedRx.id,
          );

          if (rxToDelete) {
            // Get the value of the Rx to delete
            const rxValue = Number(rxToDelete.rxPrice?.netValue || 0);

            // Filter the Rx out of the bill
            const updatedRxs = bill.rxs.filter(
              (rx) => rx.id !== localSelectedRx.id,
            );

            // If there are still Rxs in this bill after deletion, return the updated bill
            if (updatedRxs.length > 0) {
              return {
                ...bill,
                rxs: updatedRxs,
                // Recalculate the correction amount by subtracting the value of the deleted Rx
                correctionAmount: roundToNearestHundred(
                  bill.correctionAmount - rxValue,
                ),
              };
            }
            // If no Rxs left in this bill, return null (it will be filtered out below)
            return null;
          }

          // If this bill doesn't contain the Rx, return it unchanged
          return bill;
        })
        .filter(Boolean) as ProcessedCorrectionBillingDto[]; // Filter out null values

      setCorrectionBills(updatedBills);
      setTableVisible(updatedBills.length > 0);

      notificationStore.showMessage("Korrektur-Rezept entfernt", "success");
      props.onRxDelete?.(localSelectedRx.id);
    } catch (error: any) {
      let errorMessage =
        error?.response?.data?.message || error.message || "Unbekannter Fehler";
      console.error(
        "Error removing Rx from correction bills in handleDeleteConfirm",
        error,
      );
      notificationStore.showMessage(errorMessage, "error");
    } finally {
      setLocalSelectedRx(null);
      setOpenDeleteDialog(false);
    }
  };

  // Creating the bills from selected RXs from TransactionSpotlightSearch
  const handleRxsFromTransactionSelected = async (
    selectedRxPreviews: RxPreviewDto[],
  ) => {
    if (selectedRxPreviews.length === 0) return;

    setIsLoading(true);
    try {
      // Extract RX IDs from selected previews
      const rxIds = selectedRxPreviews.map((rx) => rx.id);

      // Check for duplicate RXs first
      const existingRxIds = correctionBills.flatMap((bill) =>
        bill.rxs.map((rx) => rx.id),
      );
      const newRxIds = rxIds.filter((id) => !existingRxIds.includes(id));

      if (newRxIds.length === 0) {
        notificationStore.showMessage(
          "Alle ausgewählten Rezepte sind bereits hinzugefügt",
          "warning",
        );
        setIsLoading(false);
        return;
      }

      // Prepare correction bills for the new RXs using the API
      const newPreparedBills = await prepareCorrectionBillsFromRxs({
        rxIds: newRxIds,
        accountId: account,
      });

      if (newPreparedBills.length === 0) {
        notificationStore.showMessage(
          "Keine neuen Korrektur-Rechnungen gefunden",
          "warning",
        );
        setIsLoading(false);
        return;
      }

      // If we don't have any existing bills, just set the new ones
      if (correctionBills.length === 0) {
        setCorrectionBills(newPreparedBills);
        setTableVisible(true);
        notificationStore.showMessage(
          `${newPreparedBills.length} Korrektur-Rechnungen wurden hinzugefügt`,
          "success",
        );
        setIsLoading(false);
        return;
      }

      // Merge the new bills with existing ones based on payer and previousBillNumber
      const updatedBills = [...correctionBills];
      let addedRxCount = 0;

      newPreparedBills.forEach((newBill) => {
        // Find if there's an existing bill with the same payer and previousBillNumber
        const existingBillIndex = updatedBills.findIndex(
          (bill) =>
            bill.payer.ik === newBill.payer.ik &&
            bill.previousBillNumber === newBill.previousBillNumber,
        );

        if (existingBillIndex >= 0) {
          // Add RXs to the existing bill
          const existingBill = updatedBills[existingBillIndex];

          // Filter out any RXs that might already exist in the bill (extra safety check)
          const newRxsToAdd = newBill.rxs.filter(
            (newRx) =>
              !existingBill.rxs.some(
                (existingRx) => existingRx.id === newRx.id,
              ),
          );

          if (newRxsToAdd.length > 0) {
            // Update the bill with the new RXs and recalculate the correction amount
            updatedBills[existingBillIndex] = {
              ...existingBill,
              rxs: [...existingBill.rxs, ...newRxsToAdd],
              correctionAmount:
                existingBill.correctionAmount +
                newRxsToAdd.reduce(
                  (sum, rx) => sum + Number(rx.rxPrice?.netValue || 0),
                  0,
                ),
            };
            addedRxCount += newRxsToAdd.length;
          }
        } else {
          // This is a new bill, add it to the list
          updatedBills.push(newBill);
          addedRxCount += newBill.rxs.length;
        }
      });

      setCorrectionBills(updatedBills);
      setTableVisible(true);

      notificationStore.showMessage(
        `${addedRxCount} Rezept(e) zu Korrektur-Rechnungen hinzugefügt`,
        "success",
      );
    } catch (error: any) {
      let errorMessage =
        error?.response?.data?.message || error.message || "Unbekannter Fehler";
      console.error("Error processing selected RXs from transaction", error);
      notificationStore.showMessage(errorMessage, "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Notes update (keep local copy for instant typing)
  const handleNotesChange = (payerIK: string, val: string) => {
    setNotes((prev) => ({ ...prev, [payerIK]: val }));
  };

  const handleSave = async () => {
    if (correctionBills.length === 0) {
      notificationStore.showMessage(
        "Keine Korrektur-Rechnungen zum Speichern vorhanden",
        "error",
      );
      return;
    }

    setIsSaving(true);
    try {
      const savedIds: string[] = [];

      for (const correctionBill of correctionBills) {
        const { rxs, payer, correctionAmount } = correctionBill;
        const payerIK = payer.ik;
        const rxIds = rxs.map((rx) => rx.id!).filter(Boolean);

        const dto: CreateInsuranceTransactionDto = {
          payerId: payerIK,
          accountId: account,
          amount: correctionAmount,
          userId,
          method: transactionMethods.VERSICHERUNG,
          rxIds: rxIds,
          notes: notes[payerIK] || "",
          paperCenterId: payer.paperReceivingCenter?.ik || "",
          dataCenterId: payer.digitalReceivingCenter?.ik || "",
          tAccountId: rxs[0]?.tarifGroupId === "Privat" ? 4105 : 4101,
          billType: BillingTypeEnum.CORRECTION,
        };

        const result = await createTransaction(dto);
        if (result?.id) savedIds.push(result.id);
      }

      notificationStore.showMessage(
        "Korrektur-Transaktionen erfolgreich erstellt",
        "success",
      );

      if (onBillsSaved && savedIds.length > 0) onBillsSaved(savedIds);

      // Reset UI
      setCorrectionBills([]);
      setTableVisible(false);
    } catch (error) {
      console.error("Error creating correction transactions", error);
      notificationStore.showMessage(
        "Fehler beim Erstellen der Korrektur-Transaktionen",
        "error",
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Remove-all button
  const handleRemoveAll = async () => {
    // If you want to reflect this server-side, you could PATCH removeRxIds: draft.rxSnapshots.map(s=>s.rxId)
    setCorrectionBills([]);
    setTableVisible(false);
    notificationStore.showMessage(
      "Alle Korrektur-Rechnungen entfernt",
      "success",
    );
  };

  return {
    // states
    anchorEl,
    selectedRxId,
    openDeleteDialog,
    openSingleRxDialog,
    rxNumberInput: rxNumber,
    tableVisible,
    isLoading,
    isSaving,
    correctionBills,
    notes,

    // setters
    setSelectedRxId,
    setRxNumberInput,
    setOpenSingleRxDialog,

    // handlers
    handleSave,
    handleDeleteCancel,
    handleDeleteConfirm,
    handleDeleteClick,
    handleMenuOpen,
    handleMenuClose,
    handleRemoveAll,
    handleNotesChange,
    handleRxsFromTransactionSelected, // Handler for transaction search

    // passthrough props
    onRowClick,
    userName,
    handleFullScreenDialogClose,
    account,
    userId,
    onRxDelete,
    onValidationFilter,
  };
};

export default useCorrectionBillInfo;
